/**
 * HTTP client for our own backend.
 *
 * Not used yet — backend 'rest' is not a working mode until the server in
 * zivapp-server/ exists. This is here so the migration starts from a shape
 * rather than a blank file. See docs/MIGRATION-50K.md.
 *
 * The token lives in memory and is mirrored to whatever persistence the host
 * app supplied, because web has a synchronous localStorage and React Native
 * has an asynchronous AsyncStorage. Keeping the in-memory copy authoritative
 * means getAccessToken() stays synchronous on both; restoreAccessToken() is
 * the async boot step that repopulates it.
 */

import { ZivError, ErrorCode } from '../errors.js'
import { getConfig } from '../../config.js'

const TOKEN_KEY = 'ziv.token'

let accessToken = null

const store = () => getConfig().tokenStorage ?? null

export function setAccessToken(token) {
  accessToken = token
  const s = store()
  if (!s) return
  Promise.resolve(token ? s.set(TOKEN_KEY, token) : s.remove(TOKEN_KEY))
    .catch(() => { /* persistence is best-effort; the session still works */ })
}

export function getAccessToken() {
  return accessToken
}

/** Restore a saved token at boot. Call before the first authenticated request. */
export async function restoreAccessToken() {
  const s = store()
  if (!s) return null
  try {
    accessToken = (await s.get(TOKEN_KEY)) ?? null
  } catch {
    accessToken = null
  }
  return accessToken
}

/**
 * @param {string} path   e.g. '/api/chats'
 * @param {RequestInit & { json?: unknown }} [options]
 */
export async function request(path, { json, headers, ...options } = {}) {
  const token = getAccessToken()

  let response
  try {
    response = await fetch(`${getConfig().apiUrl}${path}`, {
      ...options,
      headers: {
        ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers
      },
      body: json !== undefined ? JSON.stringify(json) : options.body
    })
  } catch (cause) {
    throw new ZivError(ErrorCode.NETWORK, 'No connection.', { cause })
  }

  if (response.status === 204) return null

  const payload = await response.json().catch(() => null)
  if (response.ok) return payload

  /*
   * The server's own code wins when it sent one.
   *
   * A status code is often too coarse to act on. A wrong OTP and an
   * expired session are both 401, and mapping both to UNAUTHENTICATED
   * meant someone mistyping a code on a brand new account was told "You
   * are signed out. Please sign in again." — about an account they had
   * never had. The server knows which it is; this lets it say so.
   */
  const code = KNOWN_CODES.has(payload?.code)
    ? payload.code
    : statusToCode(response.status)

  throw new ZivError(code, payload?.message ?? 'Request failed.')
}

/**
 * Codes the server is allowed to name.
 *
 * An allow-list rather than trusting the field: this value is used in
 * `if` branches all over the app, and a typo on the server should fall
 * back to the status mapping instead of producing an error nothing
 * matches.
 */
const KNOWN_CODES = new Set(Object.values(ErrorCode))

function statusToCode(httpStatus) {
  switch (httpStatus) {
    case 400: return ErrorCode.INVALID_INPUT
    case 401: return ErrorCode.UNAUTHENTICATED
    case 403: return ErrorCode.PERMISSION
    case 404: return ErrorCode.NOT_FOUND
    case 409: return ErrorCode.CONFLICT
    case 429: return ErrorCode.RATE_LIMITED
    default:  return httpStatus >= 500 ? ErrorCode.NETWORK : ErrorCode.UNKNOWN
  }
}

export const get   = path => request(path)
export const post  = (path, json) => request(path, { method: 'POST', json })
export const put   = (path, json) => request(path, { method: 'PUT', json })
export const patch = (path, json) => request(path, { method: 'PATCH', json })
export const del   = path => request(path, { method: 'DELETE' })
